import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  LocationIcon,
  MobileIcon,
  SearchIcon,
  TrophyIcon,
  WhatsAppIcon,
} from "../Icons";
import constants from "../../utils/constants";
import getUserLocation from "../../utils/location";
import {
  formatCityName,
  getCanonicalCitySlug,
} from "../../config/cityData";
import { HOMEPAGE_INDORE_FEATURED_ENTRIES } from "../../config/homepageIndoreFeaturedUsernames";
import { fetchGraphQL } from "../../utils/cityApi";
import { GET_ALL_TURFS } from "../../utils/queries";
import { buildTurfDetailRoute } from "../../utils/turfRoutes";
import { SportTabKey, TAB_TO_SPORT } from "../../utils/sportTabs";
import { transformTurfData } from "../../utils/transformTurfData";
import { deriveVenueSports } from "../../utils/sport";
import { getVisiblePitches } from "../../utils/turfVisibility";
import styles from "./HomepageLanding.module.scss";
import homePageHeroImage from "../../../public/homePageImage.png";

const SelectLocation = dynamic(() => import("../SelectLocation"), { ssr: false });

export interface HomepageCityCard {
  slug: string;
  name: string;
  state: string;
  turfCount: number | null;
  priceFrom: number | null;
  imageUrl: string | null;
  highlightVenue: string | null;
}

export interface HomepageVenueCard {
  id: string;
  name: string;
  cityName: string;
  citySlug: string;
  areaName: string | null;
  address: string | null;
  imageUrl: string;
  priceFrom: number | null;
  href: string;
  isPartner: boolean;
  supportedSports?: string[];
  rainProtected: boolean;
  hasCafe: boolean;
  /** Indore curated strip: replaces default amenity badges when set. */
  promoBadges?: string[];
}

export interface HomepageFaq {
  question: string;
  answer: string;
}

interface HomepageLandingProps {
  cityDirectory: HomepageCityCard[];
  featuredCities: HomepageCityCard[];
  featuredVenues: HomepageVenueCard[];
  stats: Array<{
    value: string;
    label: string;
    detail: string;
  }>;
  faqs: HomepageFaq[];
}

interface OwnerLeadFormState {
  name: string;
  phone: string;
  city: string;
  venue: string;
}

interface HomepageSportTurfQueryResponse {
  turfs?: {
    turfs?: Array<{
      supportedSports?: string[];
      Pitch?: Array<{
        sport?: string | null;
        supportedSports?: string[] | null;
        hidePitch?: boolean | null;
      }>;
    }>;
  };
}

interface HomepageVenueQueryResponse {
  turfs?: {
    turfs?: any[];
    totalCount?: number;
  };
}

const sportsCards = [
  {
    tab: "turfs" as SportTabKey,
    title: "Cricket turfs",
    detail: "Explore cricket and box cricket venues with verified photos, pricing and playing-area details.",
    iconSrc: "/Icon/batball-icon.png",
  },
  {
    tab: "football" as SportTabKey,
    title: "Football venues",
    detail: "Discover venues that support football games, group bookings and local matches.",
    iconSrc: "/Icon/football-icon.png",
  },
  {
    tab: "courts" as SportTabKey,
    title: "Pickleball courts",
    detail: "Browse supported cities for pickleball courts with the same booking-first city flow.",
    iconSrc: "/Icon/pickle-ball%20icon.png",
  },
  {
    tab: "badminton" as SportTabKey,
    title: "Badminton venues",
    detail: "Check badminton venues with venue cards, location details and booking-ready discovery.",
    iconSrc: "/Icon/batball-icon.png",
  },
];

const advantageRows = [
  {
    title: "Exact venue pricing",
    copy: "Compare venues at the exact listed price without hidden convenience fees added on top.",
  },
  {
    title: "Verified venue discovery",
    copy: "Users can browse verified listings with photos, amenities, pricing, address and contact details in one place.",
  },
  {
    title: "No commission booking flow",
    copy: "TurfBooking is built to help users discover and book venues without extra commission layered over venue pricing.",
  },
  {
    title: "Sports community layer",
    copy: "Along with venue discovery, TurfBooking is building a sports community where players can stay connected and discover updates.",
  },
];

const trustNotes = [
  "Exact venue price with no hidden charges or added commission.",
  "Verified venue listings with photos, amenities, prices and location details.",
  "Join the TurfBooking WhatsApp community for games, updates and local sports connections.",
];

const communityJoinHref = "https://chat.whatsapp.com/FFezG9pHkYvJXBhB91P9gL?mode=gi_t";
const heroHighlights = [
  "Book turfs, courts and sports venues across cities",
  "No hidden charges and no commission on venue pricing",
  "Join a growing sports community on WhatsApp",
];
const communityHighlights = [
  "Meet players, discover games and stay connected with local sports activity.",
  "Get venue updates, shared match plans and community conversations in one place.",
  "Use TurfBooking for booking and the community for ongoing engagement beyond a single slot.",
];
const mobileHeroSlides = [
  {
    tag: "Cricket and football",
    title: "Book sports venues across cities",
    copy: "Discover premium turfs and courts with a sharper, mobile-first booking flow.",
    stat: "33+ cities",
    note: "Live city discovery",
    iconSrc: "/Icon/football-icon.png",
  },
  {
    tag: "Transparent pricing",
    title: "No hidden charges. No commission.",
    copy: "Compare slots at the exact venue price before you step into the booking journey.",
    stat: "Exact pricing",
    note: "No added markup",
    iconSrc: "/Icon/batball-icon.png",
  },
  {
    tag: "Community powered",
    title: "Book slots and join the local sports community",
    copy: "Stay connected on WhatsApp for games, updates and player conversations after booking.",
    stat: "Beyond booking",
    note: "Players and updates",
    iconSrc: "/Icon/pickle-ball%20icon.png",
    target: "community",
  },
].map((slide, index) => ({
  ...slide,
  target: slide.target || (index < 2 ? "cities" : "community"),
})) as Array<{
  tag: string;
  title: string;
  copy: string;
  stat: string;
  note: string;
  iconSrc: string;
  target: "cities" | "community";
}>;

const getHomepageStartingPrice = (rawTurf: any): number | null => {
  const pitchPrices = Array.isArray(rawTurf?.Pitch)
    ? getVisiblePitches<any>(rawTurf.Pitch).map((pitch: any) => Number(pitch?.basePrice) || 0)
        .filter((price: number) => Number.isFinite(price) && price > 0)
    : [];

  if (!pitchPrices.length) return null;
  return Math.min(...pitchPrices);
};

const createHomepageVenueCard = (
  rawTurf: any,
  citySlugFallback?: string
): HomepageVenueCard | null => {
  if (!rawTurf || rawTurf?.deletedTurf || rawTurf?.hideTurf) return null;

  const fallbackSlug = getCanonicalCitySlug(citySlugFallback || rawTurf?.city || constants.DEFAULT_CITY);
  const transformed = transformTurfData(rawTurf, formatCityName(fallbackSlug));
  const firstPitch = transformed?.data?.areaPrice?.[0];

  if (!transformed?.userName || !firstPitch?.pitchName) {
    return null;
  }

  return {
    id: `${fallbackSlug}-${transformed.userName}-${rawTurf?.id || firstPitch.pitchName}`,
    name: transformed.name,
    cityName: formatCityName(fallbackSlug),
    citySlug: fallbackSlug,
    areaName: transformed.data.areaName || null,
    address: transformed.data.address || null,
    imageUrl:
      transformed.imageUrl?.[0] ||
      rawTurf?.coverImage ||
      rawTurf?.images?.[0] ||
      "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/placeholder-turf.jpg",
    priceFrom:
      firstPitch.price?.weekday?.regularHours || getHomepageStartingPrice(rawTurf),
    href: buildTurfDetailRoute({
      city: fallbackSlug,
      userName: transformed.userName,
      pitchName: firstPitch.pitchName,
      areaName: transformed.data.areaName || undefined,
    }),
    isPartner: Boolean(transformed.data.partnership),
    supportedSports: transformed.supportedSports || [],
    rainProtected: Boolean(transformed.data.isRainProtected),
    hasCafe: Boolean(transformed.data.hasCafe),
  };
};

const formatPrice = (value: number | null): string => {
  if (!value) return "Venue price";
  return `From Rs ${value}/hr`;
};

const formatStatDisplayValue = (label: string, value: string): string => {
  if (label.trim().toLowerCase() === "hidden charges") {
    return value.startsWith("₹") ? value : `₹${value}`;
  }

  return value;
};

const getVenueBadges = (venue: HomepageVenueCard): string[] => {
  if (venue.promoBadges?.length) {
    return [...venue.promoBadges].slice(0, 3);
  }

  const badges: string[] = [];

  if (venue.isPartner) badges.push("Partner venue");
  if (venue.supportedSports?.includes('FOOTBALL')) badges.push("Football");
  if (venue.rainProtected) badges.push("Rain protected");
  if (venue.hasCafe) badges.push("Cafe onsite");

  return badges.slice(0, 3);
};

const getPromoBadgeKind = (
  label: string
): "recommended" | "featured" | "partnered" | "default" => {
  const t = label.trim().toLowerCase();
  if (t === "recommended") return "recommended";
  if (t === "featured") return "featured";
  if (t === "partnered" || t === "partner") return "partnered";
  return "default";
};

function HomepageLanding({
  cityDirectory,
  featuredCities,
  featuredVenues,
  stats,
  faqs,
}: HomepageLandingProps) {
  const router = useRouter();
  const statsStripRef = useRef<HTMLElement | null>(null);
  const trustRowRef = useRef<HTMLDivElement | null>(null);
  const cityGridRef = useRef<HTMLDivElement | null>(null);
  const sportGridRef = useRef<HTMLDivElement | null>(null);
  const processGridRef = useRef<HTMLDivElement | null>(null);
  const venueGridRef = useRef<HTMLDivElement | null>(null);
  const [savedCity, setSavedCity] = useState<string | null>(null);
  const [popularVenues, setPopularVenues] = useState<HomepageVenueCard[]>(featuredVenues);
  const [popularVenueCitySlug, setPopularVenueCitySlug] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [pendingSportTab, setPendingSportTab] = useState<SportTabKey | null>(null);
  const [showAllCities, setShowAllCities] = useState(false);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [heroAutoplayVersion, setHeroAutoplayVersion] = useState(0);
  const [shouldAnimateStats, setShouldAnimateStats] = useState(false);
  const [animatedStats, setAnimatedStats] = useState<string[]>(() =>
    stats.map((stat) => {
      const numericValue = Number.parseInt(String(stat.value).replace(/\D/g, ""), 10);
      return Number.isFinite(numericValue) ? "0" : stat.value;
    })
  );
  const [ownerLead, setOwnerLead] = useState<OwnerLeadFormState>({
    name: "",
    phone: "",
    city: "",
    venue: "",
  });

  const fetchPopularVenuesForCity = useCallback(async (citySlug: string) => {
    const normalizedCitySlug = getCanonicalCitySlug(citySlug);
    const cityDisplayName = formatCityName(normalizedCitySlug);

    try {
      if (normalizedCitySlug === "indore") {
        const results = await Promise.all(
          HOMEPAGE_INDORE_FEATURED_ENTRIES.map(({ username }) =>
            fetchGraphQL<HomepageVenueQueryResponse>(GET_ALL_TURFS, {
              city: cityDisplayName,
              username,
              verificationStatus: "VERIFIED",
              limit: 1,
              offset: 0,
            })
          )
        );

        const curated: HomepageVenueCard[] = [];
        for (let i = 0; i < HOMEPAGE_INDORE_FEATURED_ENTRIES.length; i += 1) {
          const rawTurf = results[i]?.turfs?.turfs?.[0];
          const card = rawTurf ? createHomepageVenueCard(rawTurf, normalizedCitySlug) : null;
          if (card) {
            curated.push({
              ...card,
              promoBadges: [...HOMEPAGE_INDORE_FEATURED_ENTRIES[i].promoBadges],
            });
          }
        }

        if (curated.length) {
          setPopularVenues(curated);
          setPopularVenueCitySlug(normalizedCitySlug);
        }
        return;
      }

      const metaResult = await fetchGraphQL<HomepageVenueQueryResponse>(GET_ALL_TURFS, {
        city: cityDisplayName,
        verificationStatus: "VERIFIED",
        limit: 1,
        offset: 0,
      });

      const totalCount =
        typeof metaResult?.turfs?.totalCount === "number" ? metaResult.turfs.totalCount : 0;

      if (!totalCount) return;

      const batchSize = Math.min(12, totalCount);
      const maxOffset = Math.max(totalCount - batchSize, 0);
      const randomOffset = maxOffset > 0 ? Math.floor(Math.random() * (maxOffset + 1)) : 0;

      const venueResult = await fetchGraphQL<HomepageVenueQueryResponse>(GET_ALL_TURFS, {
        city: cityDisplayName,
        verificationStatus: "VERIFIED",
        limit: batchSize,
        offset: randomOffset,
      });

      const randomizedVenues = [...(venueResult?.turfs?.turfs || [])]
        .sort(() => Math.random() - 0.5)
        .map((rawTurf) => createHomepageVenueCard(rawTurf, normalizedCitySlug))
        .filter(Boolean)
        .slice(0, 6) as HomepageVenueCard[];

      if (!randomizedVenues.length) return;

      setPopularVenues(randomizedVenues);
      setPopularVenueCitySlug(normalizedCitySlug);
    } catch {
      // Keep static fallback venues when dynamic fetch fails.
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedCity = window.localStorage.getItem("selectedCity");
    if (storedCity) {
      const normalizedCity = getCanonicalCitySlug(storedCity);
      setSavedCity(normalizedCity);
      fetchPopularVenuesForCity(normalizedCity);
    }

    const autoLocationPromptKey = "homepageAutoLocationPrompted";
    if (window.sessionStorage.getItem(autoLocationPromptKey)) return;

    window.sessionStorage.setItem(autoLocationPromptKey, "true");
    getUserLocation(
      (location: any) => {
        const detectedCity = getCanonicalCitySlug(location?.city || constants.DEFAULT_CITY);
        setSavedCity(detectedCity);
        fetchPopularVenuesForCity(detectedCity);
      },
      () => {}
    );
  }, [fetchPopularVenuesForCity]);

  useEffect(() => {
    setPopularVenues(featuredVenues);
  }, [featuredVenues]);

  const quickCities = cityDirectory.slice(0, 6);
  const cityDirectoryRest = cityDirectory.slice(6);
  const compactCityDirectory = showAllCities ? cityDirectoryRest : cityDirectoryRest.slice(0, 10);

  /** City slug for "View all in …" when all listed venues share one city (or from dynamic fetch). */
  const popularVenuesViewAllSlug = useMemo(() => {
    if (popularVenueCitySlug) return popularVenueCitySlug;
    const firstSlug = popularVenues[0]?.citySlug;
    if (
      firstSlug &&
      popularVenues.every(
        (v) => getCanonicalCitySlug(v.citySlug || "") === getCanonicalCitySlug(firstSlug)
      )
    ) {
      return getCanonicalCitySlug(firstSlug);
    }
    return null;
  }, [popularVenueCitySlug, popularVenues]);

  const popularVenueCards = popularVenues.map((venue) => (
    <Link key={venue.id} href={venue.href} className={styles.venueCard}>
      <div className={styles.venueMedia}>
        <img src={venue.imageUrl} alt={venue.name} loading="lazy" />
        {venue.promoBadges?.length ? (
          <div className={styles.venuePromoBadges}>
            {venue.promoBadges.map((badge) => (
              <span
                key={badge}
                className={styles.venuePromoBadge}
                data-kind={getPromoBadgeKind(badge)}
              >
                {badge}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className={styles.venueBody}>
        <div className={styles.venueHeader}>
          <div>
            <span className={styles.venueLocation}>
              {venue.cityName}
              {venue.areaName ? `, ${venue.areaName}` : ""}
            </span>
            <h3>{venue.name}</h3>
          </div>
          <span className={styles.venuePrice}>{formatPrice(venue.priceFrom)}</span>
        </div>

        {venue.address && <p className={styles.venueAddress}>{venue.address}</p>}

        {!venue.promoBadges?.length && (
          <div className={styles.venueBadges}>
            {getVenueBadges(venue).map((badge) => (
              <span key={badge}>{badge}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  ));

  const popularVenuesHeadingBlock =
    popularVenuesViewAllSlug ? (
      <div className={styles.venueSectionHeadingRow}>
        <div className={styles.sectionHeading}>
          <h2>{`Popular venues in ${formatCityName(popularVenuesViewAllSlug)}`}</h2>
        </div>
        <Link href={`/${popularVenuesViewAllSlug}`} className={styles.venueViewAllLink}>
          {`View all in ${formatCityName(popularVenuesViewAllSlug)}`}
          <ArrowRightIcon size={14} />
        </Link>
      </div>
    ) : (
      <div className={styles.sectionHeading}>
        <h2>
          {popularVenueCitySlug
            ? `Popular venues in ${formatCityName(popularVenueCitySlug)}`
            : "Popular venues"}
        </h2>
      </div>
    );

  /** No personalized city from geo/localStorage fetch — show city browse in the popular slot; hide duplicate #home-cities below. */
  const showBrowseNearbyInsteadOfPopular = popularVenueCitySlug === null;

  const renderBrowseNearbyCities = (attachCityGridRef: boolean) => (
    <>
      <div className={styles.sectionHeading}>
        <h2>Browse nearby venues in the cities.</h2>
      </div>
      <div
        className={styles.cityGrid}
        {...(attachCityGridRef ? { ref: cityGridRef } : {})}
      >
        {featuredCities.map((city) => (
          <Link key={city.slug} href={`/${city.slug}`} className={styles.cityCard}>
            <div className={styles.cityCardMedia}>
              {city.imageUrl ? (
                <img src={city.imageUrl} alt={city.name} loading="lazy" />
              ) : (
                <div className={styles.cityFallback}>
                  <span>{city.name.slice(0, 2).toUpperCase()}</span>
                </div>
              )}
              <span className={styles.cityCardBadge}>{city.state}</span>
            </div>

            <div className={styles.cityCardBody}>
              <div>
                <h3>{city.name}</h3>
                <p>
                  {city.turfCount ? `${city.turfCount}+ live venues` : "Live city listing"}
                  {city.highlightVenue ? ` · ${city.highlightVenue}` : ""}
                </p>
              </div>
              <div className={styles.cityCardMeta}>
                <span>{formatPrice(city.priceFrom)}</span>
                <span>
                  Explore city <ArrowRightIcon size={14} />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {cityDirectoryRest.length > 0 && (
        <div className={styles.cityDirectoryWrap}>
          <div className={styles.directoryHeader}>
            <h3>More cities on TurfBooking</h3>
            <p>
              Explore more city pages to see available turfs, court options and local venue discovery.
            </p>
          </div>
          <div className={styles.cityDirectoryList}>
            {compactCityDirectory.map((city) => (
              <Link key={city.slug} href={`/${city.slug}`} className={styles.directoryChip}>
                <span>{city.name}</span>
                <small>{city.state}</small>
              </Link>
            ))}
          </div>
          {cityDirectoryRest.length > 10 && (
            <button
              type="button"
              className={styles.directoryToggle}
              onClick={() => setShowAllCities((current) => !current)}
            >
              <span>{showAllCities ? "Show fewer cities" : "Show more cities"}</span>
              <ArrowRightIcon size={14} />
            </button>
          )}
        </div>
      )}
    </>
  );

  const whatsappOwnerLeadHref = useMemo(() => {
    const lines = [
      "Hello TurfBooking team, I want to list my turf on the platform.",
      ownerLead.name ? `Name: ${ownerLead.name}` : null,
      ownerLead.phone ? `Phone: ${ownerLead.phone}` : null,
      ownerLead.city ? `City: ${ownerLead.city}` : null,
      ownerLead.venue ? `Venue name: ${ownerLead.venue}` : null,
    ].filter(Boolean);

    return `https://wa.me/${constants.BUISNESS_NUMBER}?text=${encodeURIComponent(
      lines.join("\n")
    )}`;
  }, [ownerLead.city, ownerLead.name, ownerLead.phone, ownerLead.venue]);
  const heroSlidesWithImages = useMemo(
    () => {
      const bannerBackgrounds = [
        ...featuredVenues.map((venue) => venue.imageUrl).filter(Boolean),
        ...featuredCities.map((city) => city.imageUrl).filter(Boolean),
      ] as string[];

      const bannerPositions = ["center center", "center 38%", "center 28%"];

      return mobileHeroSlides.map((slide, index) => ({
        ...slide,
        imageUrl: bannerBackgrounds[index] || bannerBackgrounds[0] || null,
        imagePosition: bannerPositions[index] || "center center",
      }));
    },
    [featuredCities, featuredVenues]
  );

  useEffect(() => {
    setHeroSlideIndex(0);
  }, [heroSlidesWithImages.length]);

  useEffect(() => {
    if (shouldAnimateStats) return;

    setAnimatedStats(
      stats.map((stat) => {
        const numericValue = Number.parseInt(String(stat.value).replace(/\D/g, ""), 10);
        return Number.isFinite(numericValue) ? "0" : stat.value;
      })
    );
  }, [shouldAnimateStats, stats]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const section = statsStripRef.current;
    if (!section || shouldAnimateStats) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldAnimateStats(true);
        observer.disconnect();
      },
      {
        threshold: 0.35,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, [shouldAnimateStats]);

  useEffect(() => {
    if (!shouldAnimateStats) return;

    const frameIds: number[] = [];
    const timers = stats.map((stat, index) => {
      const target = Number.parseInt(String(stat.value).replace(/\D/g, ""), 10);
      if (!Number.isFinite(target)) return 0;
      const hasPlus = String(stat.value).includes("+");
      const duration = 2100 + index * 420;
      const start = performance.now();

      const tick = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const nextValue = Math.round(target * eased);

        setAnimatedStats((current) => {
          const next = [...current];
          next[index] = `${nextValue}${hasPlus ? "+" : ""}`;
          return next;
        });

        if (progress < 1) {
          frameIds[index] = requestAnimationFrame(tick);
        }
      };

      frameIds[index] = requestAnimationFrame(tick);
      return frameIds[index];
    });

    return () => {
      timers.forEach((id) => id && cancelAnimationFrame(id));
      frameIds.forEach((id) => id && cancelAnimationFrame(id));
    };
  }, [shouldAnimateStats, stats]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    if (heroSlidesWithImages.length <= 1) return;

    const intervalId = window.setInterval(() => {
      setHeroSlideIndex((current) => (current + 1) % heroSlidesWithImages.length);
    }, 2800);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [heroAutoplayVersion, heroSlidesWithImages.length]);

  const mobileHorizontalScrollKey = useMemo(
    () =>
      [
        featuredCities.map((c) => c.slug).join("|"),
        popularVenues.map((v) => v.id).join("|"),
      ].join("::"),
    [featuredCities, popularVenues]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const scrollables = [
      venueGridRef.current,
      sportGridRef.current,
      processGridRef.current,
      cityGridRef.current,
    ].filter(Boolean) as HTMLDivElement[];

    const intervals = scrollables.map((container) =>
      window.setInterval(() => {
        if (!container || container.scrollWidth <= container.clientWidth + 8) return;

        const firstCard = container.children[0] as HTMLElement | undefined;
        const gap =
          Number.parseFloat(window.getComputedStyle(container).columnGap || "") ||
          Number.parseFloat(window.getComputedStyle(container).gap || "") ||
          12;
        const step = (firstCard?.getBoundingClientRect().width || container.clientWidth * 0.78) + gap;
        const maxScroll = container.scrollWidth - container.clientWidth;
        const nextLeft = container.scrollLeft + step >= maxScroll - 4 ? 0 : container.scrollLeft + step;

        container.scrollTo({
          left: nextLeft,
          behavior: "smooth",
        });
      }, 2800)
    );

    return () => {
      intervals.forEach((id) => window.clearInterval(id));
    };
  }, [mobileHorizontalScrollKey]);

  const handleDetectLocation = () => {
    setIsDetecting(true);

    getUserLocation(
      (location: any) => {
        const detectedCity = getCanonicalCitySlug(
          location?.city || constants.DEFAULT_CITY
        );

        if (typeof window !== "undefined") {
          window.localStorage.setItem("selectedCity", detectedCity);
        }

        setSavedCity(detectedCity);
        setIsDetecting(false);
        router.push(`/${detectedCity}`);
      },
      () => {
        setIsDetecting(false);
        setShowLocationModal(true);
      }
    );
  };

  const resolveHomeCitiesScrollTarget = () => {
    if (typeof document === "undefined") return null;
    const mobile = document.getElementById("home-cities-mobile");
    const desktop = document.getElementById("home-cities");
    const isShown = (el: HTMLElement | null) =>
      !!el && el.getClientRects().length > 0 && window.getComputedStyle(el).display !== "none";
    if (isShown(mobile)) return mobile;
    if (isShown(desktop)) return desktop;
    return mobile || desktop;
  };

  const handleBrowseCities = () => {
    const element = resolveHomeCitiesScrollTarget();
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToSection = (sectionId: string, offset = 0) => {
    if (typeof document === "undefined") return;
    const element =
      sectionId === "home-cities" ? resolveHomeCitiesScrollTarget() : document.getElementById(sectionId);
    if (element) {
      const absoluteTop =
        element.getBoundingClientRect().top + window.scrollY + offset;
      window.scrollTo({
        top: Math.max(absoluteTop, 0),
        behavior: "smooth",
      });
    }
  };

  const handleHeroSlideSelect = (nextIndex: number) => {
    setHeroSlideIndex(nextIndex);
    setHeroAutoplayVersion((current) => current + 1);
  };

  const handlePreviousHeroSlide = () => {
    handleHeroSlideSelect(
      heroSlideIndex === 0 ? heroSlidesWithImages.length - 1 : heroSlideIndex - 1
    );
  };

  const handleNextHeroSlide = () => {
    handleHeroSlideSelect((heroSlideIndex + 1) % heroSlidesWithImages.length);
  };

  const handleHeroBannerClick = (target: "cities" | "community") => {
    if (target === "community") {
      scrollToSection("home-community", 16);
      return;
    }

    scrollToSection("home-cities");
  };

  const handleOwnerLeadChange = (
    field: keyof OwnerLeadFormState,
    value: string
  ) => {
    setOwnerLead((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleEmailClick = () => {
    if (typeof window === "undefined") return;
    window.location.href =
      "mailto:info@turfbooking.in?subject=List%20my%20venue%20on%20TurfBooking";
  };

  const resolveSportTabForCity = async (
    citySlug: string,
    desiredTab: SportTabKey
  ): Promise<SportTabKey> => {
    if (desiredTab === "turfs") return "turfs";

    const desiredSport = TAB_TO_SPORT[desiredTab];
    if (!desiredSport) return "turfs";

    try {
      const result = await fetchGraphQL<HomepageSportTurfQueryResponse>(GET_ALL_TURFS, {
        city: formatCityName(citySlug),
        verificationStatus: "VERIFIED",
        limit: 100,
        offset: 0,
      });

      return result?.turfs?.turfs?.some((turf) =>
        deriveVenueSports({ pitches: getVisiblePitches<any>(turf?.Pitch) })
          .includes(desiredSport)
      )
        ? desiredTab
        : "turfs";
    } catch {
      return "turfs";
    }
  };

  const handleSportCardClick = (tab: SportTabKey) => {
    setPendingSportTab(tab);
    setShowLocationModal(true);
  };

  const handleSportCitySelect = async (selectedCity: string) => {
    const citySlug = getCanonicalCitySlug(selectedCity);
    const resolvedTab = await resolveSportTabForCity(citySlug, pendingSportTab || "turfs");

    if (typeof window !== "undefined") {
      window.localStorage.setItem("selectedCity", citySlug);
    }

    setSavedCity(citySlug);
    setPendingSportTab(null);
    setShowLocationModal(false);

    if (resolvedTab === "turfs") {
      await router.push(`/${citySlug}`);
      return;
    }

    await router.push({
      pathname: `/${citySlug}`,
      query: { tab: resolvedTab },
    });
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
    setPendingSportTab(null);
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroBackground} aria-hidden="true">
          <div className={styles.heroOrbPrimary} />
          <div className={styles.heroOrbSecondary} />
          <div className={styles.heroGrid} />
        </div>

        <div className={styles.heroContent}>
          <div className={styles.mobileHeroShowcase}>
            <div className={styles.mobileHeroCarouselWrap}>
              <div className={styles.mobileHeroRail}>
                <div
                  className={styles.mobileHeroTrack}
                  style={{ transform: `translate3d(-${heroSlideIndex * 100}%, 0, 0)` }}
                >
                  {heroSlidesWithImages.map((slide, index) => (
                    <button
                      type="button"
                      key={slide.title}
                      className={`${styles.mobileHeroSlide} ${styles[`mobileHeroSlideTheme${index + 1}`]}`}
                      onClick={() => handleHeroBannerClick(slide.target)}
                      aria-label={`${slide.title}. Open related section.`}
                    >
                      <div className={styles.mobileHeroSlideImage}>
                        {slide.imageUrl ? (
                          <img
                            src={slide.imageUrl}
                            alt={slide.title}
                            className={styles.mobileHeroSlideBg}
                            style={{ objectPosition: slide.imagePosition }}
                            loading={slide.title === heroSlidesWithImages[0]?.title ? "eager" : "lazy"}
                          />
                        ) : (
                          <Image
                            src={homePageHeroImage}
                            alt={slide.title}
                            fill
                            priority={slide.title === heroSlidesWithImages[0]?.title}
                            sizes="100vw"
                            className={styles.mobileHeroSlideBg}
                            style={{ objectPosition: slide.imagePosition }}
                          />
                        )}
                      </div>
                      <div className={styles.mobileHeroSlideOverlay} />
                      <div className={styles.mobileHeroSlideGlow} aria-hidden="true" />
                      <div className={styles.mobileHeroSlideMesh} aria-hidden="true" />
                      <div className={styles.mobileHeroSlideContent}>
                        <div className={styles.mobileHeroSlideTop}>
                          <span className={styles.mobileHeroSlideTag}>{slide.tag}</span>
                          <div className={styles.mobileHeroSlideIcon}>
                            <Image src={slide.iconSrc} alt="" width={18} height={18} unoptimized />
                          </div>
                        </div>
                        <h2>{slide.title}</h2>
                        <p>{slide.copy}</p>
                        <div className={styles.mobileHeroSlideFooter}>
                          <span className={styles.mobileHeroSlideStat}>{slide.stat}</span>
                          <span className={styles.mobileHeroSlideNote}>{slide.note}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {heroSlidesWithImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className={`${styles.mobileHeroArrow} ${styles.mobileHeroArrowLeft}`}
                    onClick={handlePreviousHeroSlide}
                    aria-label="Show previous banner"
                  >
                    <span aria-hidden="true">‹</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.mobileHeroArrow} ${styles.mobileHeroArrowRight}`}
                    onClick={handleNextHeroSlide}
                    aria-label="Show next banner"
                  >
                    <span aria-hidden="true">›</span>
                  </button>
                </>
              )}
            </div>
            <div className={styles.mobileHeroIndicators}>
              {heroSlidesWithImages.map((slide, index) => (
                <button
                  type="button"
                  key={slide.title}
                  className={`${styles.mobileHeroDot} ${index === heroSlideIndex ? styles.mobileHeroDotActive : ""}`}
                  aria-label={`Show banner ${index + 1}`}
                  aria-pressed={index === heroSlideIndex}
                  onClick={() => handleHeroSlideSelect(index)}
                />
              ))}
            </div>

            <div className={styles.mobileHeroCtaRow}>
              <div className={styles.heroActions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setShowLocationModal(true)}
                >
                  <SearchIcon size={18} />
                  <span>Choose city</span>
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={handleDetectLocation}
                >
                  <LocationIcon size={18} />
                  <span>{isDetecting ? "Locating..." : "Use location"}</span>
                </button>
              </div>
            </div>

            {showBrowseNearbyInsteadOfPopular && featuredCities.length > 0 && (
              <div id="home-cities-mobile" className={styles.mobilePopularVenues}>
                {renderBrowseNearbyCities(true)}
              </div>
            )}
            {!showBrowseNearbyInsteadOfPopular && popularVenues.length > 0 && (
              <div className={styles.mobilePopularVenues}>
                {popularVenuesHeadingBlock}
                <div className={styles.venueGrid} ref={venueGridRef}>
                  {popularVenueCards}
                </div>
              </div>
            )}

            <button
              type="button"
              className={styles.mobileOwnerStrip}
              onClick={() => scrollToSection("home-owner")}
            >
              <span className={styles.mobileOwnerStripGlow} aria-hidden="true" />
              <span className={styles.mobileOwnerStripMesh} aria-hidden="true" />
              <div className={styles.mobileOwnerStripIconRing}>
                <TrophyIcon size={17} />
              </div>
              <div className={styles.mobileOwnerStripMain}>
                <div className={styles.mobileOwnerStripRow}>
                  <span className={styles.mobileOwnerStripEyebrow}>Venue partners</span>
                  <span className={styles.mobileOwnerStripBadge}>Free listing</span>
                </div>
                <span className={styles.mobileOwnerStripTitle}>Are you a turf owner?</span>
                <span className={styles.mobileOwnerStripSub}>
                  Put your pitch on the map—more visibility, no listing fee.
                </span>
              </div>
              <span className={styles.mobileOwnerStripArrow} aria-hidden>
                <ArrowRightIcon size={16} />
              </span>
            </button>
          </div>

          <div className={styles.heroCopy}>
            <span className={styles.heroKicker}>Book venues and join the community</span>
            <h1 className={styles.heroTitle}>
              <span className={styles.heroTitleLine}>Book turfs, courts &amp; sports venues</span>
              <span className={styles.heroTitleAccent}>At exact venue price.</span>
            </h1>
            <p className={styles.heroText}>
              Discover verified cricket turfs, football grounds, pickleball courts and badminton venues across cities. Compare photos, pricing, timings and amenities, book without hidden charges or commission, and join the TurfBooking sports community on WhatsApp.
            </p>
            <p className={styles.mobileHeroText}>
              Compare verified venues, book at exact venue price and join the TurfBooking community.
            </p>

            <div className={styles.trustRow} ref={trustRowRef}>
              {trustNotes.map((note) => (
                <div key={note} className={styles.trustPill}>
                  <CheckCircleIcon size={18} />
                  <p>{note}</p>
                </div>
              ))}
            </div>

            <div className={styles.heroActions}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => setShowLocationModal(true)}
              >
                <SearchIcon size={18} />
                <span>Choose city</span>
              </button>

              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleDetectLocation}
              >
                <LocationIcon size={18} />
                <span>{isDetecting ? "Locating..." : "Use location"}</span>
              </button>

              <a
                href={communityJoinHref}
                target="_blank"
                rel="noreferrer"
                className={styles.ghostButton}
              >
                <WhatsAppIcon size={18} />
                <span>Join WhatsApp community</span>
              </a>
            </div>

            <div className={styles.quickRow}>
              <div className={styles.quickRowHeader}>
                <span className={styles.quickLabel}>Quick start</span>
                <button type="button" className={styles.quickLinkButton} onClick={handleBrowseCities}>
                  Browse top cities
                  <ArrowRightIcon size={14} />
                </button>
              </div>
              <div className={styles.quickLinks}>
                {savedCity && (
                  <Link href={`/${savedCity}`} className={styles.quickChip}>
                    Continue in {formatCityName(savedCity)}
                  </Link>
                )}
                {quickCities.map((city) => (
                  <Link key={city.slug} href={`/${city.slug}`} className={styles.quickChip}>
                    {city.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.heroVisual}>
            <div className={styles.visualShell}>
              <div className={styles.visualBrandRow}>
                <span className={styles.visualBadge}>Mobile-first booking</span>
                <span className={styles.visualBadge}>Community powered</span>
              </div>
              <div className={styles.visualPanel}>
                <div>
                  <span className={styles.visualEyebrow}>Why users choose TurfBooking</span>
                  <h2 className={styles.visualTitle}>Simple discovery, honest pricing and a stronger sports community.</h2>
                </div>
                <div className={styles.visualChecklist}>
                  {heroHighlights.map((item, index) => (
                    <div key={item}>
                      <strong>{`0${index + 1}`}</strong>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.heroImageFrame}>
              <Image
                src={homePageHeroImage}
                alt="Football, cricket, badminton and pickleball action on TurfBooking"
                fill
                priority
                sizes="(max-width: 767px) 100vw, (max-width: 1099px) 42vw, 520px"
                className={styles.heroImage}
              />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={statsStripRef} className={styles.statsStrip} aria-label="Homepage trust metrics">
        {stats.map((stat, index) => (
          <div key={stat.label} className={styles.statCard}>
            <div className={styles.statCardTop}>
              <div className={styles.statCardCopy}>
                <span className={styles.statValue}>
                  {formatStatDisplayValue(stat.label, animatedStats[index] || stat.value)}
                </span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            </div>
            <p className={styles.statDetail}>{stat.detail}</p>
          </div>
        ))}
      </section>

      {/* <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <span className={styles.sectionKicker}>Venue discovery</span>
          <h2>Find the right venue without endless calls and guesswork.</h2>
          <p>
            TurfBooking is built to help players discover venues faster. Start with a city,
            explore verified options, compare venue details and open the one that fits your
            game, budget and location.
          </p>
        </div>

        <div className={styles.discoveryGrid}>
          {discoveryCards.map((card) => (
            <article key={card.title} className={styles.discoveryCard}>
              <span>{card.eyebrow}</span>
              <h3>{card.title}</h3>
              <p>{card.copy}</p>
            </article>
          ))}
        </div>
      </section> */}

      {((showBrowseNearbyInsteadOfPopular && featuredCities.length > 0) ||
        (!showBrowseNearbyInsteadOfPopular && popularVenues.length > 0)) && (
        <section
          id={showBrowseNearbyInsteadOfPopular ? "home-cities" : undefined}
          className={`${styles.section} ${styles.sectionTightTop} ${styles.popularVenuesDesktopSection}`}
        >
          {showBrowseNearbyInsteadOfPopular
            ? renderBrowseNearbyCities(false)
            : (
                <>
                  {popularVenuesHeadingBlock}
                  <div className={styles.venueGrid}>{popularVenueCards}</div>
                </>
              )}
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeadingCompact}>
          <span className={styles.sectionKicker}>Sport entry points</span>
          <h2>Explore by sport and continue into city listings.</h2>
        </div>

        <div className={styles.sportGrid} ref={sportGridRef}>
          {sportsCards.map((sport) => (
            <button
              key={sport.title}
              className={styles.sportCard}
              type="button"
              onClick={() => handleSportCardClick(sport.tab)}
            >
              <div className={styles.sportIconWrap}>
                <Image src={sport.iconSrc} alt={sport.title} width={28} height={28} unoptimized />
              </div>
              <h3>{sport.title}</h3>
              <p>{sport.detail}</p>
              <span>
                View in city listing <ArrowRightIcon size={14} />
              </span>
            </button>
          ))}
        </div>
      </section>

          <section className={styles.section}>
        <div className={styles.sectionHeadingCompact}>
          <span className={styles.sectionKicker}>How it works</span>
          <h2>From city selection to venue detail in three steps.</h2>
        </div>

        <div className={styles.processGrid} ref={processGridRef}>
          <article className={styles.processCard}>
            <strong>01</strong>
            <h3>Pick your city</h3>
            <p>Detect your location or manually choose the city where you want to play.</p>
          </article>
          <article className={styles.processCard}>
            <strong>02</strong>
            <h3>Compare the shortlist</h3>
            <p>Use city pages, area filters, sport tabs and venue tags to compare the best options.</p>
          </article>
          <article className={styles.processCard}>
            <strong>03</strong>
            <h3>Open the venue page</h3>
            <p>Review timings, pitch or court details, pricing and booking actions before confirming.</p>
          </article>
        </div>
      </section>

      {!showBrowseNearbyInsteadOfPopular && (
        <section className={`${styles.section} ${styles.sectionTightTop}`} id="home-cities">
          {renderBrowseNearbyCities(true)}
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeadingCompact}>
          <span className={styles.sectionKicker}>Why players choose TurfBooking</span>
          <h2>Built for easier booking, clearer pricing and a better sports experience.</h2>
        </div>

        <div className={styles.advantageGrid}>
          {advantageRows.map((item) => (
            <article key={item.title} className={styles.advantageCard}>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.communitySection} id="home-community">
          <div className={styles.communityCopy}>
            <span className={styles.sectionKicker}>Join the community</span>
            <h2>Book your venue and stay connected with players beyond the booking.</h2>
            <p>
              TurfBooking is not only about venue discovery. We are also building a sports community where users can connect, discover updates and stay close to the local game.
            </p>
            <div className={styles.communityHighlights}>
              {communityHighlights.map((item) => (
                <div key={item} className={styles.communityHighlight}>
                  <TrophyIcon size={18} />
                  <p>{item}</p>
                </div>
              ))}
            </div>
            <div className={styles.communityActions}>
              <a
                href={communityJoinHref}
                target="_blank"
                rel="noreferrer"
                className={`${styles.primaryButton} ${styles.whatsappGlowButton}`}
              >
                <WhatsAppIcon size={18} />
                <span>Join WhatsApp community</span>
              </a>
              <button type="button" className={styles.secondaryButton} onClick={() => setShowLocationModal(true)}>
                <SearchIcon size={18} />
                <span>Start booking by city</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.ownerSection} id="home-owner">
        <div className={styles.ownerCopy}>
          <span className={styles.sectionKicker}>List your turf</span>
          <h2>Own or manage a sports venue? Get discovered on TurfBooking.</h2>
          <p>
            If you manage a turf or sports venue, share your details with TurfBooking and let
            local players discover your venue through city pages and listing routes.
          </p>
          {/* <div className={styles.ownerActions}>
            <a href={`tel:+91${constants.BUISNESS_NUMBER}`} className={styles.primaryButton}>
              <MobileIcon size={18} />
              <span>Call +91 {constants.BUISNESS_NUMBER}</span>
            </a>
            <button type="button" onClick={handleEmailClick} className={styles.secondaryButton}>
              <span>Email info@turfbooking.in</span>
            </button>
          </div> */}
        </div>

        <div className={styles.ownerFormCard}>
          <div className={styles.ownerFormHeader}>
            <div className={styles.ownerFormHeaderTop}>
              <span className={styles.ownerFormEyebrow}>Venue onboarding</span>
              <span className={styles.ownerFormFreeBadge}>Free listing</span>
            </div>
            <h3>Send your turf details on WhatsApp</h3>
            <p>
              Share your contact, city and venue name to list your turf on TurfBooking for free. The TurfBooking team can connect with you and take the onboarding forward.
            </p>
          </div>

          <div className={styles.ownerFormGrid}>
            <label className={styles.inputField}>
              <span>Your name</span>
              <input
                type="text"
                value={ownerLead.name}
                onChange={(event) => handleOwnerLeadChange("name", event.target.value)}
                placeholder="Your full name"
              />
            </label>
            <label className={styles.inputField}>
              <span>Phone number</span>
              <input
                type="tel"
                value={ownerLead.phone}
                onChange={(event) => handleOwnerLeadChange("phone", event.target.value)}
                placeholder="9876543210"
              />
            </label>
            <label className={styles.inputField}>
              <span>City</span>
              <input
                type="text"
                value={ownerLead.city}
                onChange={(event) => handleOwnerLeadChange("city", event.target.value)}
                placeholder="Indore"
              />
            </label>
            <label className={styles.inputField}>
              <span>Venue name</span>
              <input
                type="text"
                value={ownerLead.venue}
                onChange={(event) => handleOwnerLeadChange("venue", event.target.value)}
                placeholder="Your turf name"
              />
            </label>
          </div>

          <div className={styles.ownerFormActions}>
            <a href={whatsappOwnerLeadHref} target="_blank" rel="noreferrer" className={styles.primaryButton}>
              <WhatsAppIcon size={18} />
              <span>Send on WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeadingCompact}>
          <span className={styles.sectionKicker}>FAQs</span>
          <h2>Frequently asked questions about TurfBooking.</h2>
        </div>

        <div className={styles.faqList}>
            {faqs.map((faq) => (
              <details key={faq.question} className={styles.faqItem}>
                <summary
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span>{faq.question}</span>
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
        </div>
      </section>

      {showLocationModal && (
        <SelectLocation
          visible={showLocationModal}
          onClose={handleLocationModalClose}
          onCitySelect={pendingSportTab ? handleSportCitySelect : undefined}
        />
      )}

    </div>
  );
}

export default HomepageLanding;
